import { HEADERS_NAMES, API_ERRORS } from "~/utils/constants";
import { getApiToken, refreshApiToken } from "~/composables/auth";
import { useAuthStorage } from "~/composables/useAuthStorage";
import { useToast } from "~/composables/useToast";
import { refreshNuxtData, clearNuxtData } from "#imports";

const RELOG_REQUIRED_RESPONSE_CODES = [
  API_ERRORS.API_TOKEN_FAILED,
  API_ERRORS.E_API_TOKEN_NOT_FOUND,
  API_ERRORS.E_X_CLIENT_ERROR,
];

export default defineNuxtPlugin((nuxtApp) => {
  const { public: config } = useRuntimeConfig();
  const FF_USER_AUTH_ENABLED = useFeatureFlag("userAuthEnabled");

  const {
    getToken,
    getClientUUID,
    setToken,
    setClientUUID,
    getProfilePicture,
    setProfilePicture,
    clearAuth,
    initializeFromStorage,
  } = useAuthStorage();
  const toast = useToast();

  // Initialize from storage on client side
  if (import.meta.client) {
    initializeFromStorage();
  }

  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve();
      }
    });
    failedQueue = [];
  };

  const handleQueuedRequest = (request, options) => {
    return new Promise((resolve, reject) => {
      failedQueue.push({
        resolve: () => {
          const token = getToken();
          const clientUUID = getClientUUID();

          setAuthHeaders(options.headers, { token, clientUUID });
          resolve(nuxtApp.$api(request, options));
        },
        reject,
      });
    });
  };

  const setAuthHeaders = (headers, { token, clientUUID } = {}) => {
    if (token) {
      headers.set(HEADERS_NAMES.authToken, token);
    }
    if (clientUUID) {
      headers.set(HEADERS_NAMES.clientUUID, clientUUID);
    }
  };

  const handleSessionExpired = () => {
    clearAuth();
    clearNuxtData("user-data");
    toast.add({
      title: "Odhlášení",
      text: "Vaše relace vypršela. Přihlaste se prosím znovu.",
      variant: "warning",
    });
  };

  const api = $fetch.create({
    baseURL: config.API_DOMAIN,
    credentials: "include",

    onRequest({ options }) {
      options.headers = new Headers(options.headers);

      // Only add auth headers on client side
      if (import.meta.client && FF_USER_AUTH_ENABLED) {
        setAuthHeaders(options.headers, {
          token: getToken(),
          clientUUID: getClientUUID(),
        });
      }
    },

    async onResponse({ request, response, options }) {
      if (import.meta.server) {
        return;
      }

      const responseCode = response._data?.code;

      if (!RELOG_REQUIRED_RESPONSE_CODES.includes(responseCode)) {
        return;
      }

      if (isRefreshing) {
        return handleQueuedRequest(request, options);
      }

      isRefreshing = true;

      try {
        // 1. Attempt token refresh
        const refreshed = await refreshApiToken();

        if (refreshed?.token) {
          setToken(refreshed.token);
          refreshNuxtData("user-data");
          processQueue(null);
          setAuthHeaders(options.headers, { token: refreshed.token });
          return nuxtApp.$api(request, options);
        } else {
        }
      } catch (error) {
        // 2. Refresh failed — show toast if user was logged in
        const { isLogged } = useUser();
        if (isLogged.value) {
          handleSessionExpired();
        }
      }

      // 3. Fallback: create new anonymous token
      try {
        const newTokens = await getApiToken();

        if (newTokens?.token) {
          setToken(newTokens.token);
          setClientUUID(newTokens.clientUUID);
          // Identity dropped to anonymous — don't leave the previous user's
          // avatar URL on COOKIES_DOMAIN, where websale can read it.
          if (getProfilePicture()) setProfilePicture(null);
          processQueue(null);
          setAuthHeaders(options.headers, {
            token: newTokens.token,
            clientUUID: newTokens.clientUUID,
          });
          return nuxtApp.$api(request, options);
        }

        processQueue(new Error("Token creation failed"));
        return Promise.reject(new Error("Token creation failed"));
      } catch (e) {
        processQueue(e);
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    },
  });

  return {
    provide: {
      api: api,
    },
  };
});
